/**
 * Shared utility functions for shift template operations
 */
import { format, isValid, differenceInMinutes } from 'date-fns';
import { DayShift } from '@/types';

/**
 * Safe date parsing utility
 */
export const safeParseDate = (dateValue: unknown): Date => {
  try {
    if (!dateValue) return new Date();
    const date = new Date(dateValue as string);
    return isValid(date) ? date : new Date();
  } catch (error) {
    return new Date();
  }
};

/**
 * Safe date formatting utility
 */
export const safeFormat = (date: Date, formatString: string): string => {
  try {
    return isValid(date) ? format(date, formatString) : '--:--';
  } catch (error) {
    return '--:--';
  }
};

/**
 * Calculate total duration for shifts
 */
export const calculateTotalDuration = (shifts: DayShift[]): number => {
  return shifts.reduce((total, shift) => {
    const startTime = safeParseDate(shift.start_time);
    const endTime = safeParseDate(shift.end_time);
    return total + Math.max(0, differenceInMinutes(endTime, startTime));
  }, 0);
};

/**
 * Format duration in minutes to hours and minutes
 */
export const formatDuration = (totalMinutes: number): string => {
  if (totalMinutes <= 0) return '0h 0m';

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours > 0 && minutes > 0) {
    return `${hours}h ${minutes}m`;
  } else if (hours > 0) {
    return `${hours}h`;
  } else {
    return `${minutes}m`;
  }
};

/**
 * Validate template name
 */
export const validateTemplateName = (name: string): { isValid: boolean; error?: string } => {
  const trimmedName = name.trim();

  if (!trimmedName) {
    return { isValid: false, error: 'Template name is required' };
  }

  if (trimmedName.length < 2) {
    return { isValid: false, error: 'Template name must be at least 2 characters' };
  }

  if (trimmedName.length > 50) {
    return { isValid: false, error: 'Template name must be 50 characters or less' };
  }

  return { isValid: true };
};

/**
 * Format shift time range display
 */
export const formatShiftTimeRange = (shift: DayShift): string => {
  const startTime = safeParseDate(shift.start_time);
  const endTime = safeParseDate(shift.end_time);
  const startFormatted = safeFormat(startTime, 'HH:mm');
  const endFormatted = safeFormat(endTime, 'HH:mm');
  return `${startFormatted} - ${endFormatted}`;
};